import cn from 'classnames';
import { NextPage } from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';
import { useUserAgent } from 'next-useragent';
import { useRouter } from 'next/router';
import { SocialManager } from '@data/social/SocialManager';
import { ExpectedProps, ServerPropsFactory } from '@lib/factory/serverprops';
import { LinksEnum } from '@specs/navigation';
import { SocialManagerView } from '@specs/views';
import { useGetIsBlocked } from '@hooks/TRPC_Hooks/useGetIsBlocked';
import { useGetIsBlockedBy } from '@hooks/TRPC_Hooks/useGetIsBlockedBy';
import { translateType, useTranslation } from '@hooks/useTranslation';
import { PageLayout } from '@components/PageLayout/PageLayout';
import { AuthorPostFeedList } from '@components/Pages/FeedPage/PostsLists/AuthorPostFeedList';
import { DesktopHeader } from '@components/Pages/ProfilePages/DesktopHeader/DesktopHeader';
import { Info } from '@components/Pages/ProfilePages/Info/Info';
import { Sorting } from '@components/Pages/ProfilePages/Sorting';
import { TabItems } from '@components/Pages/ProfilePages/TabItems';
import { ExternalGrid } from '@components/Pages/ProfilePages/ToysPage/ExternalGrid/ExternalGrid';
import { FilterSelect } from '@components/Pages/ProfilePages/ToysPage/FilterSelect';
import { MyGrid } from '@components/Pages/ProfilePages/ToysPage/MyGrid/MyGrid';
import { Search } from '@components/UIKit/Search/Search';
import s from '../profilePage.module.scss';
import { useMemo } from 'react';
const getSeoConfig = (isMyProfile: boolean, profile: SocialManagerView.publicProfile, tr: translateType): NextSeoProps => {
  const title = tr({
    id: isMyProfile ? 'profile.page.title.my' : 'profile.page.title'
  }, {
    name: profile.shortid
  });
  const description = tr({
    id: isMyProfile ? 'profile.page.description.my' : 'profile.page.description'
  }, {
    shortId: profile.shortid
  });
  const imageUrl = `${process.env.NEXT_PUBLIC_CDN_BASE_URL}/mirror/avatars/${profile.avatar?.id}.original.webp`;
  return {
    title,
    description,
    openGraph: {
      title,
      description,
      images: [{
        url: imageUrl,
        width: 156,
        height: 156,
        alt: 'R.Toys'
      }]
    },
    twitter: {
      site: '@rtoysapp',
      handle: '@rtoysapp',
      cardType: 'summary_large_image'
    },
    additionalMetaTags: [{
      name: 'twitter:title',
      content: title
    }, {
      name: 'twitter:description',
      content: description
    }, {
      name: 'twitter:image',
      content: imageUrl
    }]
  };
};
export const getServerSideProps = ServerPropsFactory(async (props: ExpectedProps) => {
  const [profile] = await Promise.all([props?.slug ? SocialManager.getPublicProfile(props?.slug) : Promise.resolve(null)]);
  const isMyProfile = props.session?.uid === profile?.id;
  return {
    ...props,
    profile: profile!,
    isMyProfile
  } satisfies PageProps;
}, [{
  key: 'profile',
  destination: LinksEnum.ERROR_404
}]);
interface PageProps extends ExpectedProps {
  profile: SocialManagerView.publicProfile;
  isMyProfile: boolean;
}
const Page: NextPage<PageProps> = ({
  profile,
  isMyProfile,
  agent
}) => {
  const router = useRouter();
  const {
    translate: tr
  } = useTranslation();
  const {
    isMobile
  } = useUserAgent(agent ?? '');
  const {
    isBlocked
  } = useGetIsBlocked({
    targetShortId: profile.shortid
  }, {
    enabled: !isMyProfile,
    trpc: {
      context: {
        skipBatch: true
      }
    },
    staleTime: 0
  });
  const {
    isBlocked: isBlockedBy
  } = useGetIsBlockedBy({
    targetShortId: profile.shortid
  }, {
    enabled: !isMyProfile,
    trpc: {
      context: {
        skipBatch: true
      }
    },
    staleTime: 0
  });
  const IsBlock = isBlocked || isBlockedBy;
  const seoConfig = useMemo(() => getSeoConfig(isMyProfile, profile, tr), [isMyProfile, profile, tr]);
  return <PageLayout data-sentry-element="PageLayout" data-sentry-component="Page" data-sentry-source-file="index.tsx">
            <NextSeo {...seoConfig} data-sentry-element="NextSeo" data-sentry-source-file="index.tsx" />

            <div className={s.profilePage}>
                <div className={cn(s.profilePage__container, '_container')}>
                    <div className={s.profilePage__info}>
                        <Info {...profile} isMyProfile={isMyProfile} data-sentry-element="Info" data-sentry-source-file="index.tsx" />
                    </div>
                    <div className={s.profilePage__body}>
                        <DesktopHeader shortid={profile.shortid} isMyProfile={isMyProfile} id={profile.id} data-sentry-element="DesktopHeader" data-sentry-source-file="index.tsx" />
                        <div className={s.profilePage__main}>
                            <div className={s.profilePage__box}>
                                <div className={s.profilePage__tabs}>
                                    <TabItems shortid={profile.shortid} data-sentry-element="TabItems" data-sentry-source-file="index.tsx" />
                                </div>
                                {!IsBlock && <>
                                        {router.pathname === LinksEnum.USER_TOYS && <Search placeholder={tr({
                  id: 'feed.page.toys.placeholder'
                })} name={'search'} />}
                                    </>}
                            </div>
                            {router.pathname === LinksEnum.USER_TOYS && <div className={cn(s.profilePage__filters, isMyProfile && s.profilePage__filters_my)}>
                                    {isMyProfile && <div className={s.profilePage__select}>
                                            <FilterSelect />
                                        </div>}
                                    <div className={s.profilePage__sorting}>
                                        {!IsBlock && <Sorting />}
                                    </div>
                                </div>}
                        </div>
                        {!IsBlock && <>
                                {router.pathname === LinksEnum.USER && <AuthorPostFeedList profileId={profile.id} isMobile={isMobile} isMyProfile={isMyProfile} />}
                                {router.pathname === LinksEnum.USER_TOYS && (isMyProfile ? <MyGrid shortid={profile.shortid} /> : typeof profile?.artistProfile?.id === 'number' && <ExternalGrid artistId={profile.artistProfile.id} />)}
                            </>}
                    </div>
                </div>
            </div>
        </PageLayout>;
};
export default Page;